import { Link } from 'react-router-dom';

export default function TOCPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center">
                <i className="ri-flask-line text-xl text-white"></i>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                PharmaBeast
              </span>
            </Link>
            <Link 
              to="/theory" 
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-teal-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>理論一覧に戻る</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* ヒーローセクション */}
      <div className="relative bg-gradient-to-br from-teal-600 via-blue-600 to-indigo-700 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-link-m text-lg"></i>
            <span className="text-sm font-medium">Theory of Constraints</span>
          </div>
          
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            TOC（制約理論）
          </h1>
          
          <p className="text-2xl mb-8 text-white/90 leading-relaxed">
            なぜ薬局は頑張っているのに、楽にならないのか
          </p>
          
          <div className="flex items-center gap-3 text-lg">
            <div className="w-1 h-8 bg-white/80 rounded-full"></div>
            <p className="font-medium">全体は「一番詰まっている所」で決まる</p>
          </div>
        </div>
      </div>

      {/* 結論セクション */}
      <div className="max-w-5xl mx-auto px-6 -mt-10 relative z-20 mb-16">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-8 shadow-xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-4">結論</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                調剤薬局の成果（安全・待ち時間・満足度・利益）は、<strong className="text-amber-700">「一番詰まっている工程（制約）」で100％決まります</strong>。全員が忙しい薬局ほど、改善が空回りしている可能性があります。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="max-w-5xl mx-auto px-6 pb-20">
        {/* 理論の解説 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-book-open-line text-xl text-white"></i>
            </div>
            理論の解説
          </h2>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 mb-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <i className="ri-focus-3-line text-teal-600"></i>
              TOC（Theory of Constraints）とは何か
            </h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              TOC（制約理論）は、物理学者・経営学者であるエリヤフ・ゴールドラットが提唱したマネジメント理論です。
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              TOCの核心は、驚くほどシンプルです。
            </p>
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-l-4 border-teal-500 p-6 rounded-lg mb-6">
              <p className="text-lg font-semibold text-slate-800">
                システム全体の成果は、たった1つの制約（ボトルネック）によって制限される。
              </p>
            </div>
            
            <p className="text-slate-700 leading-relaxed mb-4">
              ここでいう「制約」とは、
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-user-line text-sm text-teal-600"></i>
                </div>
                <span className="text-slate-700"><strong>人</strong>（鑑査できる薬剤師が1人しかいない）</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-flow-chart text-sm text-blue-600"></i>
                </div>
                <span className="text-slate-700"><strong>工程</strong>（投薬が詰まる、一包化が集中する）</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-file-list-3-line text-sm text-indigo-600"></i>
                </div>
                <span className="text-slate-700"><strong>ルール</strong>（受付を止められない、優先順位が曖昧）</span>
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              など、全体の流れを止めている一点のことです。
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-pink-50 border-2 border-red-200 rounded-2xl p-8 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-xl text-white"></i>
              </div>
              <p className="text-lg font-bold text-slate-800">
                重要なのは、👉 制約以外をどれだけ改善しても、全体は良くならないという点です。
              </p>
            </div>
          </div>
        </section>

        {/* 間違った改善 */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-600"></i>
              調剤薬局で起きている「TOC的に間違った改善」
            </h3>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              多くの薬局で、こんな改善が行われています。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-700">
                  <i className="ri-checkbox-circle-line text-slate-400"></i>
                  <span>受付を速くする</span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-700">
                  <i className="ri-checkbox-circle-line text-slate-400"></i>
                  <span>調剤を効率化する</span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-700">
                  <i className="ri-checkbox-circle-line text-slate-400"></i>
                  <span>全員に同じタスクを振る</span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 text-slate-700">
                  <i className="ri-checkbox-circle-line text-slate-400"></i>
                  <span>「手が空いたら何かやる」文化</span>
                </div>
              </div>
            </div>

            <p className="text-slate-700 leading-relaxed mb-6">
              一見、正しそうに見えます。しかしTOCの視点では、これは<strong className="text-red-600">部分最適</strong>です。
            </p>

            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border-l-4 border-red-500 mb-6">
              <h4 className="text-lg font-bold text-slate-800 mb-4">何が起きるか</h4>
              <div className="flex items-center gap-3 text-slate-700 leading-relaxed">
                <span>受付・調剤が速くなる</span>
                <i className="ri-arrow-right-line text-red-500"></i>
                <span>処方箋が大量に流れ込む</span>
                <i className="ri-arrow-right-line text-red-500"></i>
                <span>鑑査・投薬が詰まる</span>
                <i className="ri-arrow-right-line text-red-500"></i>
                <span>待ち時間が増え、ミスリスクが上がる</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-xl p-6">
              <p className="text-lg font-semibold">
                👉 頑張った結果、現場が苦しくなる。
              </p>
              <p className="mt-2 text-white/80">
                これが「改善しているのに楽にならない」正体です。
              </p>
            </div>
          </div>
        </section>

        {/* 制約の特定 */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <i className="ri-search-eye-line text-teal-600"></i>
              調剤薬局への翻訳｜制約はどこにあるのか
            </h3>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              薬局の制約は、ほぼ次のどれかに集約されます。
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-5 border-l-4 border-teal-500">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-star-line text-white"></i>
                  </div>
                  <span className="text-lg font-semibold text-slate-800">最終鑑査に立てる薬剤師</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border-l-4 border-blue-500">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-white"></i>
                  </div>
                  <span className="text-lg font-semibold text-slate-800">投薬対応できる人数・スペース</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border-l-4 border-indigo-500">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white"></i>
                  </div>
                  <span className="text-lg font-semibold text-slate-800">一包化・施設・在宅対応が集中する時間帯</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-6">
              <p className="text-lg text-slate-800 leading-relaxed">
                ここで大事なのは、<strong className="text-amber-700">「制約は1つだけ」</strong>という前提です。<br />
                あれもこれも詰まっているように見えても、一番影響が大きい<strong className="text-amber-700">"主制約"は1つ</strong>です。
              </p>
            </div>
          </div>
        </section>

        {/* TOCの5ステップ */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-list-ordered text-xl text-white"></i>
            </div>
            TOCの5ステップ｜薬局向け超実務版
          </h2>

          <p className="text-slate-700 leading-relaxed mb-8">
            TOCには有名な「5つのステップ」があります。これを薬局用の言葉に翻訳します。
          </p>

          <div className="space-y-6">
            {/* ステップ1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-teal-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-white">①</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">制約を特定する</h3>
                  <div className="space-y-2 mb-4">
                    <p className="text-slate-700">「今、一番待ちが発生している工程はどこか？」</p>
                    <p className="text-slate-700">「患者体験を止めているのはどこか？」</p>
                  </div>
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                    <p className="text-lg font-semibold text-teal-800">
                      👉 ほぼ確実に <strong>鑑査</strong> or <strong>投薬</strong> です。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-blue-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-white">②</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">制約を最大限活かす</h3>
                  <p className="text-slate-700 mb-4">制約工程に、ムダな仕事をさせない。</p>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-blue-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">鑑査担当に電話対応・雑務をさせない</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-blue-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">投薬中に割り込みを入れない</span>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-lg font-semibold text-blue-800">
                      👉 制約は"王様"。最優先で守る。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-indigo-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-white">③</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">他工程を制約に従属させる</h3>
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-4 mb-4">
                    <p className="text-lg font-bold text-amber-800">ここが一番重要で、一番できていない。</p>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-indigo-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">受付・調剤は「鑑査・投薬が処理できる量だけ」流す</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-indigo-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">入れすぎない勇気を持つ</span>
                    </div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <p className="text-lg font-semibold text-indigo-800">
                      👉 これは後述するリトルの法則（WIP制御）そのものです。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ4 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-purple-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-white">④</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">制約を強化する</h3>
                  <p className="text-slate-700 mb-4">初めてここで</p>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-user-add-line text-purple-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">人を増やす</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-time-line text-purple-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">時間を増やす</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-tools-line text-purple-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-slate-700">ツールを入れる</span>
                    </div>
                  </div>
                  <p className="text-slate-700 mb-4">を検討します。</p>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-lg font-semibold text-purple-800">
                      👉 いきなり増員するのは、TOC的には最終手段。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ステップ5 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-pink-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-white">⑤</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">制約が移動したら、最初に戻る</h3>
                  <p className="text-slate-700 mb-4">制約は改善すると必ず移動します。</p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-slate-600">昨日は</span>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-lg font-semibold">鑑査</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-600">今日は</span>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-lg font-semibold">投薬</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-600">次は</span>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-lg font-semibold">一包化</span>
                    </div>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                    <p className="text-lg font-semibold text-pink-800">
                      👉 改善は終わらないプロセスです。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KPI */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-teal-600"></i>
              KPI｜TOCで見るべき数字はこれだけ
            </h3>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              TOCでは、細かいKPIを大量に追いません。<br />
              最低限、次の3つで十分です。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border-2 border-teal-200">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-speed-line text-2xl text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-800 mb-2">制約工程の処理能力</h4>
                <p className="text-slate-600">（枚/時）</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-stack-line text-2xl text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-800 mb-2">制約前のWIP</h4>
                <p className="text-slate-600">（仕掛品）</p>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200">
                <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-pause-circle-line text-2xl text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-slate-800 mb-2">制約工程の中断回数</h4>
                <p className="text-slate-600">（回/日）</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <p className="text-lg text-slate-800">
                これだけで、<strong className="text-teal-600">「なぜ今日は荒れているか」</strong>が説明できます。
              </p>
            </div>
          </div>
        </section>

        {/* 明日の一手 */}
        <section>
          <div className="bg-gradient-to-br from-teal-600 via-blue-600 to-indigo-700 text-white rounded-2xl p-10 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <i className="ri-rocket-line text-3xl text-white"></i>
                </div>
                <h2 className="text-3xl font-bold">明日の一手｜TOCの第一歩</h2>
              </div>
              
              <p className="text-xl mb-6 text-white/90 leading-relaxed">
                明日やることは、たった1つです。
              </p>

              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-8 border-2 border-white/30 mb-6">
                <p className="text-2xl font-bold mb-4">
                  👉 「今、どこが一番詰まっているか」を全員で言語化する。
                </p>
                <p className="text-lg text-white/90">
                  ホワイトボードに「今日の制約：〇〇」と書くだけで構いません。
                </p>
              </div>

              <p className="text-lg mb-4 text-white/90">それだけで、</p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <i className="ri-close-circle-line text-2xl text-red-300"></i>
                  <span className="text-lg">無意識の入れすぎ</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <i className="ri-close-circle-line text-2xl text-red-300"></i>
                  <span className="text-lg">無駄な頑張り</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <i className="ri-close-circle-line text-2xl text-red-300"></i>
                  <span className="text-lg">事故につながる忙しさ</span>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/20">
                <p className="text-xl font-semibold text-center">
                  が、目に見えて減ります。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 関連理論へのリンク */}
        <section className="mt-16">
          <h3 className="text-2xl font-bold text-slate-800 mb-6">関連する理論</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link 
              to="/theory/dbr" 
              className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-teal-300 transition-all group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-rhythm-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">DBR</h4>
              <p className="text-sm text-slate-600">ドラム・バッファ・ロープ</p>
            </Link>
            <Link 
              to="/theory/littles-law" 
              className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-teal-300 transition-all group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-calculator-line text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">リトルの法則</h4>
              <p className="text-sm text-slate-600">WIP制御の数学的根拠</p>
            </Link>
            <Link 
              to="/theory/flow-efficiency" 
              className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-teal-300 transition-all group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-flow-chart text-2xl text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">フロー効率</h4>
              <p className="text-sm text-slate-600">Flow Efficiency</p>
            </Link>
          </div>
        </section>
      </div>

      {/* フッター */}
      <footer className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center">
                <i className="ri-flask-line text-xl text-white"></i>
              </div>
              <span className="text-xl font-bold">PharmaBeast</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/privacy-policy" className="hover:text-white transition-colors whitespace-nowrap">
                プライバシーポリシー
              </Link>
              <span>© 2025 PharmaBeast</span>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-teal-400 transition-colors whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
